/**
 * What the two sign-in screens share: where the second one lives, what the first one
 * hands it, and how an API failure becomes something a member can read.
 */

import { ApiError } from "~/lib/api";

/**
 * The code step is a route of its own, so the back control and Android's hardware
 * button both pop it and the screen owes nothing to the layout of the one before it.
 */
export const AUTH_CODE_PATH = "/auth/code";

/**
 * Carried to the code screen as router state rather than kept in a store: it is
 * worthless without the request that produced it, so a reload — which drops it — is
 * right to land back on the e-mail step.
 */
export interface CodeStepState {
  email: string;
  /** Whether verifying will create the account. Answered by `otp/request`. */
  signingUp: boolean;
  /** Only ever set when the API runs with OTP echo on, which production forbids. */
  debugCode: string | null;
  /** Known in-app path to restore after authentication, when one was requested. */
  returnTo?: string;
}

/**
 * An i18n *key*, not a message: the screens hold it in state and resolve it at render,
 * so an error raised while the UI was in Czech re-renders in English with the language.
 */
export function translateAuthError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 429) return "auth.errors.rateLimited";
    if (error.status === 401) return "auth.errors.invalidCode";
    if (error.status === 403) return "auth.errors.inactiveAccount";
    // The API validates addresses more strictly than the client regex does —
    // reserved domains, for instance. That is an address problem, not a crash.
    if (error.status === 422) return "auth.errors.invalidEmail";
    return "common.somethingWentWrong";
  }
  return "auth.errors.network";
}
